import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation, ChangeDetectionStrategy} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Field } from '../../interfaces/field';
import { Subscription } from 'rxjs';
import { StateService } from '../../services/state.service';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgxSmartModalService } from 'ngx-smart-modal';
import moment from 'moment-timezone';

@Component({
    selector: 'app-product-selection',
    templateUrl: './product-selection.component.html',
    encapsulation: ViewEncapsulation.Emulated,
    styleUrls: ['./product-selection.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProductSelectionComponent implements OnInit, OnDestroy {
  @Input() form: any;
  @Input() index: number;
  @Input() products: any;
  @Input() layout = 'page-layout';
  @Input() minimumOrderTime = 60;
  // Whether the "Request a quote" button in the booking-summary card is offered at all.
  // Set by the parent from OrderComponent.canRequestQuote() (per-form opt-in gate only —
  // see QuoteRequestService.isQuotationEnabled()).
  @Input() quotationEnabled = false;

  @Output() selectionFinished: EventEmitter<string> = new EventEmitter<string>();
  @Output() scrollUp: EventEmitter<string> = new EventEmitter<string>();
  @Output() openHelpModal: EventEmitter<any> = new EventEmitter<any>();
  // Emitted when the quote button in the booking-summary card is clicked; the parent
  // opens the quotation confirmation dialog.
  @Output() requestQuote: EventEmitter<void> = new EventEmitter<void>();
  useNewDesign = true;
  formGroup: UntypedFormGroup;
  fields: Field[];
  orderMode: string;
  orderModeSub: Subscription;
  productSummery: boolean;
  forceSummaryTag: boolean;
  productExtraDescription: boolean;
  airportDeparture: boolean;
  airportDepartureSub: Subscription;
  airportDestination: boolean;
  airportDestinationSub: Subscription;
  returnTrip = false;
  returnTripCompleted = false;
  showReturn = true;
  formChanges: Subscription;
  returnDepartureField: Field;
  returnDestinationField: Field;
  formSubscription: Subscription;
  cdnUrl = '';
  // Suffixes this component's ngx-smart-modal identifiers, mirroring
  // OrderComponent.instanceId — keeps modal ids unique if the widget is
  // embedded more than once on the same host page.
  instanceId: string;
  // The product a "next available at" click is confirming, held between opening
  // confirmPickupUpdateModal and the modal's own confirm button.
  pendingNextAvailableProduct: any;

  constructor(private state: StateService,
              private route: ActivatedRoute,
              private translate: TranslateService,
              private modalService: NgxSmartModalService) {
    this.instanceId = `wb-${Math.random().toString(36).slice(2, 11)}`;
    this.formSubscription = this.route.data.subscribe((data: {form: any, currencies: string[]}) => {
      this.cdnUrl = (data.form && data.form.config && data.form.config.cdnUrl ? data.form.config.cdnUrl : '');
    });
    this.orderModeSub = this.state.orderMode$.subscribe(
      orderMode => this.orderMode = orderMode
    );
    this.airportDepartureSub = this.state.airportDeparture$.subscribe(
      airportDeparture => this.airportDeparture = airportDeparture
    );
    this.airportDestinationSub = this.state.airportDestination$.subscribe(
      airportDestination => {
        this.airportDestination = airportDestination
      }
    );
  }
  ngOnInit() {
    this.formGroup = this.state.formGroup;
    this.showReturn = this.state.formParts[this.index].showReturn;
    this.fields = this.state.formParts[this.index].fields;
    this.productSummery = this.state.formParts[this.index].productSummery;
    this.forceSummaryTag = this.state.formParts[this.index].forceSummaryTag;
    this.productExtraDescription = this.state.formParts[this.index].productExtraDescription;
    this.returnDepartureField = this.state.returnDepartureField;
    this.returnDestinationField = this.state.returnDestinationField;
    this.formChanges = this.formGroup.valueChanges.subscribe(this.onChanges.bind(this));
    this.onChanges(this.formGroup.getRawValue());

    if(this.returnTrip) {
      this.returnTripCompleted = true;
    }
  }

  onChanges(values) {
    this.returnTrip = (values.returnTrip);
    if(!this.returnTrip) {
      this.returnTripCompleted = false;
    }
  }

  /**
   * A product TPS marked unavailable carries a `nextAvailableAt` slot. Clicking it re-quotes
   * at that time by setting the same date control the date/time field already writes to —
   * the existing formGroup.valueChanges pipeline (see OrderComponent) picks it up and
   * re-fetches product prices/availability for the new time, same as a manual date edit would.
   *
   * Must be a formatted ISO STRING, not a Date object: every other write path in
   * date-time-field.component.ts (onChange/onChangeTime/setNow) stores this control
   * as `moment(...).format()`, and DateTimeFieldComponent.ngAfterViewInit re-parses
   * the control's value by string-splitting it on 'T' whenever the field
   * re-initializes (e.g. navigating back to this step). A native Date's `.toString()`
   * has no real 'T' separator — only the 'T' inside the "Tue"/"Thu" weekday
   * abbreviation — so that split silently produced a garbage, wrong date.
   */
  selectNextAvailableSlot(product: any) {
    if (!product || !product.nextAvailableAt) {
      return;
    }
    const mode = this.formGroup.controls['requestedDateMode']?.value || 'requestedDate';
    this.formGroup.controls[mode]?.setValue(moment(product.nextAvailableAt).format());
  }

  /**
   * Mirrors requestedDateDisplay's tz/clock-format handling so a company's configured
   * timezone and 12/24h clock preference apply here too, not just the browser's local time.
   */
  formatNextAvailable(product: any): string {
    if (!product || !product.nextAvailableAt) {
      return '';
    }
    const cs = this.state.companySettings || {};
    const tz = this.form && this.form.config ? this.form.config.timeZone : null;
    let m = moment(product.nextAvailableAt);
    if (tz && moment.tz.zone(tz)) {
      m = m.tz(tz);
    }
    return m.format((cs.clock === '12' ? 'hh' : 'HH') + ':mm' + (cs.clock === '12' ? ' a' : ''));
  }

  /**
   * The "next available at" button's click handler: confirms the new pickup time
   * with the user before acting on it (moving the date is a real change to their
   * booking, not a passive display) via confirmPickupUpdateModal, the app's own
   * dialog — never a native confirm() or a one-off library popup, per the
   * "open modals via the service" rule.
   */
  confirmNextAvailableSlot(product: any): void {
    if (!product || !product.nextAvailableAt) {
      return;
    }
    this.pendingNextAvailableProduct = product;
    this.modalService.open(`confirmPickupUpdateModal${this.instanceId}`);
  }

  /**
   * confirmPickupUpdateModal's confirm button: applies the pending next-available
   * slot and proceeds exactly like a normal one-way product selection — accepting
   * this slot is the whole point of confirming, so the `available === false` guard
   * in `selectProduct` is intentionally bypassed here.
   */
  acceptNextAvailableSlot(): void {
    const product = this.pendingNextAvailableProduct;
    if (!product) {
      return;
    }
    this.pendingNextAvailableProduct = null;
    this.selectNextAvailableSlot(product);
    this.returnTrip = false;
    this.returnTripCompleted = false;
    this.formGroup.patchValue({
      returnTrip: false,
      product,
    });
    this.navigate();
  }

  selectProduct(product: any, returnTrip: boolean = false) {
    if (product && product.available === false) {
      return;
    }
    const self = this;
    this.returnTrip = returnTrip;

    this.formGroup.patchValue({
      returnTrip,
      product,
    });
    if (!returnTrip) {
      self.returnTripCompleted = false;
      this.navigate();
    } else {
      setTimeout(() => {
        self.returnTripCompleted = true;
      }, 300);
    }
  }

  /**
   * The summary-card "Request a quote" button is offered alongside the product list, not
   * gated behind picking one first (unlike the per-product Enkele reis/Retour rit buttons,
   * which call `selectProduct` themselves). Without a selected product the form's `product`
   * control stays empty and `buildOrder()` has no price/vehicleType to attach — the quote
   * email would go out with an empty product and a €0.00 price. Default to the first
   * (cheapest/only) single-trip product here, same as clicking its own button would, then
   * emit as before so the parent still opens the confirmation dialog.
   */
  requestQuoteFromSummary(): void {
    const product = this.products && this.products[0];
    if (product) {
      this.formGroup.patchValue({ returnTrip: false, product });
    }
    this.requestQuote.emit();
  }

  /**
   * Booking summary shown at the top of the quotation/product list.
   * Row 1: requested date, passengers, luggage. Row 2: departure → stopovers → destination.
   */
  get requestedDateDisplay(): string {
    if (!this.formGroup) {
      return '';
    }
    const mode = this.formGroup.controls['requestedDateMode']?.value || 'requestedDate';
    const raw = this.formGroup.controls[mode]?.value || this.formGroup.controls['requestedDate']?.value;
    if (!raw) {
      return '';
    }
    const cs = this.state.companySettings || {};
    const tz = this.form && this.form.config ? this.form.config.timeZone : null;
    const dateFormat = cs.dateFormat || 'D-M-YYYY';
    let m = moment(raw);
    if (tz && moment.tz.zone(tz)) {
      m = m.tz(tz);
    }
    return m.format(dateFormat + ' ' + (cs.clock === '12' ? 'hh' : 'HH') + ':mm' + (cs.clock === '12' ? ' a' : ''));
  }

  get passengerCount(): number {
    return this.formGroup ? this.formGroup.controls['passengerCount']?.value : null;
  }

  get luggageCount(): number {
    return this.formGroup ? this.formGroup.controls['luggage']?.value : null;
  }

  get addressList(): string[] {
    if (!this.formGroup) {
      return [];
    }
    const list: string[] = [];
    const departure = this.formatAddress(this.formGroup.controls['departure']?.value);
    if (departure) {
      list.push(departure);
    }
    const stopOvers = this.formGroup.controls['stopOvers'] as any;
    if (stopOvers && stopOvers.controls) {
      stopOvers.controls.forEach((control: any) => {
        const stopOver = this.formatAddress(control.value);
        if (stopOver) {
          list.push(stopOver);
        }
      });
    }
    const destination = this.formatAddress(this.formGroup.controls['destination']?.value);
    if (destination) {
      list.push(destination);
    }
    return list;
  }

  formatAddress(value: any): string {
    if (!value) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    return value.formatted_address || value.internationalAlias || '';
  }

  hasBookingSummary(): boolean {
    return !!this.requestedDateDisplay || this.addressList.length > 0;
  }

  navigate() {
    this.selectionFinished.emit('next');

    const self = this;
    setTimeout(() => {
      self.selectionFinished.emit('scrollUp');
    }, 500);
  }

  ngOnDestroy(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
    if (this.formChanges) {
      this.formChanges.unsubscribe();
    }
    if (this.orderModeSub) {
      this.orderModeSub.unsubscribe();
    }
    if (this.airportDepartureSub) {
      this.airportDepartureSub.unsubscribe();
    }
    if (this.airportDestinationSub) {
      this.airportDestinationSub.unsubscribe();
    }
  }

}
